/**
 * Nova hero composer (web): the "Schedule a task" card at the top of the
 * board, and — via the optional copy props — the job wizard's brief field.
 * Reuses the chat hero composer's classes verbatim
 * (.tabbio-hero-composer / -input / -controls, .tabbio-pill-icon-btn,
 * .tabbio-send-btn, .tabbio-listening) so it reads as the same family, but is
 * a plain controlled textarea: no assistant-ui runtime.
 *
 * Send slot state machine: mic (idle, no text) → arrow (has text) → spinner
 * (compiling/activating) → violet stop (dictating). Enter submits, Shift+Enter
 * inserts a newline. The mic hides entirely when the browser has no Web
 * Speech API.
 */
import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { CapabilityChips } from '../assistant-ui/capability-chips.web';
import type { AgentCapabilitiesPreference } from '../../lib/agent-capabilities-preference';
import { colors } from '@tabbio/tokens';
import { NovaListening } from '@/components/voice/NovaListening.web';
import { VoiceLevelMeter } from '@/components/voice/VoiceLevelMeter.web';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { joinTranscript } from '@/lib/voice/join-transcript';
import type { NovaCreateStatus } from './use-nova-compiler';

interface Props {
  value: string;
  onChange: (text: string) => void;
  status: NovaCreateStatus;
  onSubmit: (sentence: string) => void;
  onOpenBuilder: () => void;
  /** Call sites outside Nova (the job wizard) speak their own copy. */
  placeholder?: string;
  addLabel?: string;
  busyLabel?: string;
  /** Lets a host focus the field (validation focus hand-off). */
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  /**
   * The task's Research, Image and Result picks: with both props the chat
   * composer's chips show here in controlled mode (the Tasks board), so the
   * chat preference is never written. The job wizard's brief field omits them.
   */
  capabilities?: AgentCapabilitiesPreference;
  onCapabilitiesChange?: (next: AgentCapabilitiesPreference) => void;
}

export function NovaComposer({
  value,
  onChange,
  status,
  onSubmit,
  onOpenBuilder,
  placeholder,
  addLabel,
  busyLabel,
  inputRef: inputRefProp,
  capabilities,
  onCapabilitiesChange,
}: Props) {
  const { t } = useTranslation('common');
  const localInputRef = useRef<HTMLTextAreaElement | null>(null);
  const inputRef = inputRefProp ?? localInputRef;

  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Same hook as the chat composers (see `useVoiceInput`): the transcript
  // lands all together once the user stops.
  const voice = useVoiceInput({
    role: 'seeker',
    onTranscript: (transcript) => {
      onChangeRef.current(joinTranscript(valueRef.current, transcript));
    },
  });
  const voiceState = voice.status === 'idle' ? null : voice.status;
  const dictating = voiceState !== null;
  // Every browser we ship to has MediaRecorder; guard anyway rather than
  // render a mic that can't record.
  const voiceSupported = typeof MediaRecorder !== 'undefined';

  const busy = status !== 'idle';
  const hasText = value.trim().length > 0;
  const canSend = hasText && !busy && !dictating;

  // Autogrow: the class caps growth at max-height 200px, scrolls beyond.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [inputRef, value]);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    onSubmit(value);
  }, [canSend, onSubmit, value]);

  return (
    <div className="tabbio-hero-composer tabbio-nova-composer">
      <div className="tabbio-voice-slot">
        <textarea
          ref={inputRef}
          className="tabbio-hero-composer-input"
          rows={1}
          dir="auto"
          placeholder={dictating ? '' : (placeholder ?? t('nova.composer.placeholder'))}
          value={value}
          disabled={busy}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              handleSend();
            }
          }}
        />
        {dictating && !value ? (
          // The status reads where the placeholder would, until there is text.
          <div className="tabbio-voice-slot-overlay tabbio-voice-slot-overlay--top">
            <NovaListening
              state={voiceState ?? 'listening'}
              level={voice.level}
              hideMeter
            />
          </div>
        ) : null}
      </div>

      <div className="tabbio-hero-composer-controls">
        <div className="tabbio-hero-composer-controls-left">
          {voiceState === 'listening' ? (
            // While listening, `+` becomes the way out: stop keeps the words,
            // this throws them away.
            <button
              type="button"
              className="tabbio-pill-icon-btn"
              onClick={() => void voice.cancel()}
              aria-label={t('actions.cancel')}
            >
              <MaterialSymbol name="close" />
            </button>
          ) : (
            <button
              type="button"
              className="tabbio-pill-icon-btn"
              onClick={onOpenBuilder}
              disabled={busy || dictating}
              aria-label={addLabel ?? t('nova.composer.add')}
            >
              <MaterialSymbol name="add" />
            </button>
          )}
          {capabilities && onCapabilitiesChange ? (
            <CapabilityChips value={capabilities} onChange={onCapabilitiesChange} />
          ) : null}
        </div>

        <div className="tabbio-hero-composer-controls-right">
          {dictating && value ? (
            <NovaListening
              state={voiceState ?? 'listening'}
              level={voice.level}
              hideMeter
            />
          ) : null}
          {dictating ? (
            <>
              {voiceState === 'listening' ? <VoiceLevelMeter level={voice.level} /> : null}
              <button
                type="button"
                className="tabbio-send-btn active dictating"
                style={{ backgroundColor: colors.primary, color: '#fff' }}
                onClick={() => voice.toggle()}
                aria-label={t('chat.dictationStop', {
                  defaultValue: 'Stop dictation',
                })}
              >
                <MaterialSymbol name="stop" size={18} />
              </button>
            </>
          ) : busy ? (
            <button
              type="button"
              className="tabbio-send-btn active"
              disabled
              aria-label={busyLabel ?? t('nova.builder.compiling')}
            >
              <MaterialSymbol name="progress_activity" className="tabbio-nova-spin" />
            </button>
          ) : canSend ? (
            <button
              type="button"
              className="tabbio-send-btn active"
              onClick={handleSend}
              aria-label={t('nova.composer.send')}
            >
              <MaterialSymbol name="arrow_upward" />
            </button>
          ) : voiceSupported ? (
            <button
              type="button"
              className="tabbio-send-btn"
              onClick={() => voice.toggle()}
              aria-label={t('chat.home.dictateLabel', {
                defaultValue: 'Dictate',
              })}
            >
              <MaterialSymbol name="mic" />
            </button>
          ) : (
            <button
              type="button"
              className="tabbio-send-btn"
              disabled
              aria-label={t('nova.composer.send')}
            >
              <MaterialSymbol name="arrow_upward" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
